import { useEffect, useRef } from 'react';
import { ClientLog } from 'src/constants/clientLog';
import ClientLogger from 'src/utils/logger';
import { GATED_CLIENT_ID, useClientId } from 'src/contexts/ClientContext';
import { safePublish } from './safePublish';
import { ActionType, FeatureName, PagePerformanceMetric } from './metricsFields';

/**
 * Emits per-page performance metrics via Katal, enabling the per-page seller-experience
 * dashboard (latency, availability, fault rate) required by SU-BACKLOG-1237.
 *
 * The hook measures the elapsed time from component mount to the moment the page's data
 * finishes loading (`isLoading` transitions to false) and, at that point, publishes:
 * - a Latency timer (supports p50/p90/p99 stats),
 * - a Success counter when `isError` is false, or
 * - a Fault counter when `isError` is true.
 *
 * Each emission goes out twice: once under `{pageName}.PageLoad` and once under
 * `{pageName}.PageLoad.{Gated|Ungated}`. The undimensioned action is kept so existing iGraph
 * graphs and dashboards continue to work; the suffixed one enables the per-surface split.
 * The base action and the surface actions are separate counters covering the same events, so
 * never sum across both — use one or the other.
 *
 * In addition to the Katal/Minerva emission above, each completed load is logged once via
 * `ClientLogger.info(ClientLog.PageLoad, …)` with `{ page, surface, durationMs, isError }`. That
 * structured log is extracted by a CDK CloudWatch MetricFilter (dimensioned by Page and Surface)
 * to power a version-controlled CloudWatch dashboard and alarms. Unlike high-volume API request
 * latency, page loads are low-volume, so this is logged unsampled for accurate availability counts.
 *
 * Metrics are reported exactly once per page load — subsequent re-renders (including later
 * `isError` flips) do not emit duplicate metrics. Metric failures are swallowed and logged as
 * warnings so they never affect page functionality, mirroring the other metrics publishers.
 *
 * When a page is mounted on a route keyed by a URL param (e.g. `:learningModuleId`), React Router
 * reuses the same component instance across in-app navigations that only change the param, so the
 * component does not remount. Pass the identity of the loaded resource as `resourceKey` so the
 * measurement resets and a fresh Latency/Success/Fault is emitted for each navigation instead of
 * silently under-counting after the first load.
 *
 * @param pageName - The page these metrics belong to; used as the publisher action prefix.
 * @param isLoading - Whether the page's data is still loading. Latency is measured until this
 *   first becomes false.
 * @param isError - Whether the page failed to load its data at the moment loading completed.
 * @param resourceKey - Optional identity of the loaded resource (e.g. the URL param id). When it
 *   changes, the measurement is reset so the next load re-emits metrics. Omit for pages whose
 *   component remounts on every load.
 */
export const usePagePerformance = (
  pageName: FeatureName,
  isLoading: boolean,
  isError: boolean,
  resourceKey?: string,
): void => {
  // Read from context rather than apiSurface: ClientProvider publishes the surface in an effect
  // that runs after this hook's, so getApiSurface() would be stale on the first page load.
  const clientId = useClientId();
  // Campus-only split. Widget pages resolve clientId from a URL param the embedder controls, so
  // they are not classified here — the API-layer Surface dimension covers widget traffic.
  const surface = clientId === GATED_CLIENT_ID ? 'Gated' : 'Ungated';

  // Captured once on mount so latency is measured from mount to data-loaded, independent of
  // how many times the component re-renders in between. Reset when resourceKey changes.
  const startTimeRef = useRef<number>(performance.now());
  // Guards against duplicate emissions on re-renders after the page has loaded once.
  const hasReportedRef = useRef<boolean>(false);
  // Tracks the resource identity the current measurement belongs to, so an in-app navigation to a
  // different resource (same component instance) starts a fresh measurement.
  const lastResourceKeyRef = useRef<string | undefined>(resourceKey);

  useEffect(() => {
    // A new resource loaded into the same component instance: restart the measurement.
    if (resourceKey !== lastResourceKeyRef.current) {
      lastResourceKeyRef.current = resourceKey;
      startTimeRef.current = performance.now();
      hasReportedRef.current = false;
    }

    if (isLoading || hasReportedRef.current) {
      return;
    }
    hasReportedRef.current = true;

    const duration = performance.now() - startTimeRef.current;

    const publishUnder = (methodName: string): void => safePublish(
      methodName,
      (publisher) => {
        publisher.publishTimerMonitor(PagePerformanceMetric.Latency, duration);
        publisher.publishCounterMonitor(
          isError ? PagePerformanceMetric.Fault : PagePerformanceMetric.Success,
          1,
        );
      },
      (error) => {
        ClientLogger.warn(ClientLog.MetricsFailure, {
          context: 'Page performance metrics publishing failed - this does not affect the page',
          pageName,
          methodName,
          isError,
          error: String(error),
        });
      },
    );

    // Dual-emit: the undimensioned action keeps existing iGraph graphs working, the
    // surface-suffixed one enables the per-page Gated/Ungated split.
    const baseMethodName = `${pageName}.${ActionType.PageLoad}`;
    publishUnder(baseMethodName);
    publishUnder(`${baseMethodName}.${surface}`);

    // In addition to the Katal/Minerva emission above, log the completed load once to CloudWatch
    // Logs (via katalLogger) so a CDK MetricFilter can extract it into a CloudWatch metric
    // dimensioned by Page and Surface. This powers a version-controlled CDK CloudWatch dashboard
    // and alarms, independent of the iGraph/Minerva path. Page loads are low-volume (once per
    // load), so — unlike high-volume API request latency — this is logged unsampled for accurate
    // availability counts. Duration is guarded against non-finite/negative values (mirroring
    // latencyLogger) so a bad measurement never emits a garbage metric value.
    if (Number.isFinite(duration) && duration >= 0) {
      ClientLogger.info(ClientLog.PageLoad, {
        page: pageName,
        surface,
        durationMs: Math.round(duration),
        isError,
      });
    }
  }, [pageName, isLoading, isError, resourceKey, surface]);
};

export default usePagePerformance;
